import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  BookOpen, 
  FileText, 
  Video, 
  Headphones, 
  Download, 
  ExternalLink,
  Search,
  ChevronRight,
  Play,
  Clock,
  Star,
  Menu,
  X,
  MessageSquare,
  Zap,
  Shield,
  Users,
  Phone,
  BarChart3,
  Settings,
  HelpCircle,
  Mail,
  ArrowRight
} from "lucide-react";

const SPEAKR_LOGO = "https://customer-assets.emergentagent.com/job_voice-ai-agents-2/artifacts/8ueg8ogv_Futuristic%20Speakr%20AI%20logo%20design.png";

// Resource categories
const CATEGORIES = [
  { id: "all", name: "All Resources", icon: BookOpen },
  { id: "guides", name: "Guides", icon: FileText },
  { id: "videos", name: "Video Tutorials", icon: Video },
  { id: "webinars", name: "Webinars", icon: Headphones },
  { id: "case-studies", name: "Case Studies", icon: BarChart3 },
  { id: "downloads", name: "Downloads", icon: Download }
];

// Resources data
const RESOURCES = [
  {
    id: 1,
    title: "Getting Started with Speakr AI",
    description: "Learn how to set up your first AI voice agent in under 10 minutes. Complete walkthrough from signup to going live.",
    category: "guides",
    type: "Guide",
    readTime: "5 min read",
    featured: true,
    icon: Zap
  },
  {
    id: 2,
    title: "AI Receptionist Setup Tutorial",
    description: "Step-by-step video guide to configuring your AI receptionist for optimal call handling and customer satisfaction.",
    category: "videos",
    type: "Video",
    readTime: "12 min watch",
    featured: true,
    icon: Video
  },
  {
    id: 3,
    title: "HVAC Company Case Study: 40% More Leads",
    description: "How Northern Climate HVAC increased lead capture by 40% and reduced missed calls to zero with Speakr AI.",
    category: "case-studies",
    type: "Case Study",
    readTime: "8 min read",
    featured: true,
    icon: BarChart3
  },
  {
    id: 4,
    title: "Customizing Your AI Agent's Voice & Personality",
    description: "Deep dive into voice selection, personality tuning, and script customization for your brand.",
    category: "guides",
    type: "Guide",
    readTime: "7 min read",
    featured: false,
    icon: Settings
  },
  {
    id: 5,
    title: "Webinar: AI Voice Trends for 2025",
    description: "Join industry experts discussing the future of AI voice technology and what it means for small businesses.",
    category: "webinars",
    type: "Webinar",
    readTime: "45 min",
    featured: false,
    icon: Headphones
  },
  {
    id: 6,
    title: "Dental Practice Case Study: 50% Fewer No-Shows",
    description: "See how Maple Dental Group reduced appointment no-shows by 50% using AI-powered reminders.",
    category: "case-studies",
    type: "Case Study",
    readTime: "6 min read",
    featured: false,
    icon: BarChart3
  },
  {
    id: 7,
    title: "Integration Guide: CRM & Calendar Sync",
    description: "Connect Speakr with your existing CRM, Google Calendar, or scheduling software for seamless workflows.",
    category: "guides",
    type: "Guide",
    readTime: "10 min read",
    featured: false,
    icon: Settings
  },
  {
    id: 8,
    title: "Security & Compliance Whitepaper",
    description: "Detailed documentation of Speakr's security measures, HIPAA readiness, and data protection policies.",
    category: "downloads",
    type: "PDF Download",
    readTime: "15 pages",
    featured: false,
    icon: Shield
  },
  {
    id: 9,
    title: "Multi-Language Setup Guide",
    description: "Configure your AI agent to handle calls in English, French, Spanish, and 20+ other languages.",
    category: "guides",
    type: "Guide",
    readTime: "8 min read",
    featured: false,
    icon: Users
  },
  {
    id: 10,
    title: "ROI Calculator Spreadsheet",
    description: "Download our Excel template to calculate your potential savings and ROI with Speakr AI.",
    category: "downloads",
    type: "Excel Download",
    readTime: "Interactive",
    featured: false,
    icon: Download
  },
  {
    id: 11,
    title: "Insurance Agency Case Study",
    description: "How Summit Insurance increased quote requests by 35% with 24/7 AI-powered lead qualification.",
    category: "case-studies",
    type: "Case Study",
    readTime: "7 min read",
    featured: false,
    icon: BarChart3
  },
  {
    id: 12,
    title: "Call Flow Design Best Practices",
    description: "Expert tips on designing call flows that maximize customer satisfaction and conversion rates.",
    category: "videos",
    type: "Video",
    readTime: "18 min watch",
    featured: false,
    icon: Video
  }
];

// Help topics
const HELP_TOPICS = [
  { title: "Account & Billing", description: "Manage subscriptions, payments, and invoices", icon: FileText },
  { title: "Technical Support", description: "Troubleshoot issues and get help", icon: Settings },
  { title: "API Documentation", description: "Developer guides and API reference", icon: Zap },
  { title: "Feature Requests", description: "Suggest new features and improvements", icon: Star }
];

export default function Resources() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const navLinks = [
    { name: "Features", href: "/#features" },
    { name: "Industries", href: "/#industries" },
    { name: "Pricing", href: "/pricing" },
    { name: "Resources", href: "/resources" },
    { name: "Contact", href: "/contact" }
  ];

  const filteredResources = RESOURCES.filter(resource => {
    const matchesCategory = selectedCategory === "all" || resource.category === selectedCategory;
    const matchesSearch = searchQuery === "" || 
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredResources = RESOURCES.filter(r => r.featured);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800/50 backdrop-blur-sm sticky top-0 z-50 bg-slate-950/80">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
              <img src={SPEAKR_LOGO} alt="Speakr" className="h-10 w-auto" />
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`text-sm font-medium transition-colors ${
                    link.href === "/resources" 
                      ? "text-white" 
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-4">
              <Button variant="ghost" className="text-slate-300 hover:text-white" onClick={() => navigate("/login")}>
                Sign In
              </Button>
              <Button onClick={() => navigate("/register")} className="bg-gradient-to-r from-blue-600 to-purple-600">
                Get Started
              </Button>
            </div>

            <button
              className="md:hidden p-2 text-slate-400 hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-slate-800 pt-4">
              <nav className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="text-slate-300 hover:text-white py-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="flex flex-col gap-2 pt-4 border-t border-slate-800">
                  <Button variant="ghost" className="justify-start text-slate-300" onClick={() => navigate("/login")}>
                    Sign In
                  </Button>
                  <Button onClick={() => navigate("/register")} className="bg-gradient-to-r from-blue-600 to-purple-600">
                    Get Started
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-4 bg-purple-500/10 text-purple-400 border-purple-500/20">
            <BookOpen className="w-3 h-3 mr-1" />
            Learning Center
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Resources & Guides
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
            Everything you need to get the most out of Speakr AI. Guides, tutorials, case studies, and more.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search resources..."
              className="pl-12 py-6 bg-slate-800/50 border-slate-700 text-white text-lg"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Featured Resources */}
      <section className="pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl font-semibold text-white mb-6">Featured Resources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredResources.map((resource) => {
              const Icon = resource.icon;
              return (
                <Card 
                  key={resource.id}
                  className="bg-gradient-to-br from-slate-800/50 to-slate-800/30 border-slate-700/50 hover:border-blue-500/50 transition-all cursor-pointer group"
                >
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                        {resource.type}
                      </Badge>
                      <span className="text-xs text-slate-500">{resource.readTime}</span>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <h3 className="font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-slate-400 line-clamp-2">
                      {resource.description}
                    </p>
                    <div className="mt-4 flex items-center text-blue-400 text-sm font-medium">
                      Read more <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Category Filter & Resources Grid */}
      <section className="py-12 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    selectedCategory === category.id
                      ? "bg-blue-600 text-white"
                      : "bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-800"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {category.name}
                </button>
              );
            })}
          </div>

          {/* Resources Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => {
              const Icon = resource.icon;
              return (
                <Card 
                  key={resource.id}
                  className="bg-slate-800/30 border-slate-700/50 hover:border-slate-600/50 transition-all cursor-pointer"
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="outline" className="text-slate-400 border-slate-600">
                        {resource.type}
                      </Badge>
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {resource.readTime}
                      </span>
                    </div>
                    <h3 className="font-medium text-white mb-2">{resource.title}</h3>
                    <p className="text-sm text-slate-400 line-clamp-2">{resource.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-400">No resources found matching your criteria.</p>
              <Button 
                variant="link" 
                className="text-blue-400 mt-2"
                onClick={() => { setSelectedCategory("all"); setSearchQuery(""); }}
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Help Center */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
              <HelpCircle className="w-3 h-3 mr-1" />
              Help Center
            </Badge>
            <h2 className="text-2xl font-bold text-white">Need More Help?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {HELP_TOPICS.map((topic, idx) => {
              const Icon = topic.icon;
              return (
                <Card 
                  key={idx}
                  className="bg-slate-800/30 border-slate-700/50 hover:border-emerald-500/50 transition-all cursor-pointer"
                >
                  <CardContent className="p-5 text-center">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <h3 className="font-medium text-white mb-1">{topic.title}</h3>
                    <p className="text-xs text-slate-400">{topic.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <Card className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border-blue-500/20">
            <CardContent className="p-8 text-center">
              <MessageSquare className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-3">Still Have Questions?</h2>
              <p className="text-slate-400 mb-6">
                Our team is here to help. Reach out and we'll get back to you within 24 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={() => navigate("/contact")}
                  className="bg-gradient-to-r from-blue-600 to-purple-600"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
                <Button 
                  variant="outline" 
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                  onClick={() => navigate("/register?demo=true")}
                >
                  Book a Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800/50 py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src={SPEAKR_LOGO} alt="Speakr" className="h-8 w-auto" />
          </Link>
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Speakr™ by GSK Productions Inc. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
